import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Checkbox } from "@/lib/shadcn/ui/checkbox";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import type { StrOwnerLabel } from "@/models/responses";

const SELECT_TESTID = "str-owner-select";
const CHECKBOX_TESTID = "str-subject-checkbox";
const NEW_OWNER_NAME_ID = "str-new-owner-name";
const NEW_OWNER_USERNAME_ID = "str-new-owner-username";
const NEW_OWNER_PASSWORD_ID = "str-new-owner-password";
const ERROR_ID = "str-owner-error";
/** Sentinel value for the "＋ New owner" option (reveals the inline-create inputs). */
export const NEW_STR_OWNER = "new";

/** The inline-created owner captured on the add form (its Taxis password is write-only). */
export interface NewStrOwner {
    name: string;
    taxisUsername: string;
    taxisPassword: string;
}

const EMPTY_NEW_OWNER: NewStrOwner = { name: "", taxisUsername: "", taxisPassword: "" };

export interface StrOwnerSelection {
    /** Pure UI state: the "Subject to STR upload" checkbox (no wire field). */
    subject: boolean;
    /** The chosen owner's id, or `null` (no owner / "new owner" pending). */
    strOwnerId: number | null;
    /** The inline-create fields when "＋ New owner" is chosen, else `null`. */
    newStrOwner: NewStrOwner | null;
}

export interface StrOwnerSelectProps {
    /** UI-only "Subject to STR upload" checkbox state (checked ⟺ an owner is sent). */
    subject: boolean;
    /** The currently-chosen STR owner id, or `null`. */
    strOwnerId: number | null;
    /** The inline-create fields when "＋ New owner" is being entered, else `null`. */
    newStrOwner: NewStrOwner | null;
    /** The owners offered, in API response order. */
    owners: StrOwnerLabel[];
    /** Emits the next subject-flag + owner pair. */
    onChange: (next: StrOwnerSelection) => void;
    /** The owners query is in-flight → disable the select. */
    isLoading?: boolean;
    /** The owners query failed → disable the select. */
    isError?: boolean;
    /** An inline validation error to surface under the card (e.g. STR ⟹ MyData, EC-001). */
    error?: string | null;
}

function ownerLabel(owner: StrOwnerLabel): string {
    return owner.taxisUsername ? `${owner.name} — ${owner.taxisUsername}` : owner.name;
}

/**
 * "Short-Term Registry (STR)" card: a "Subject to STR upload" checkbox and, when
 * checked, the STR-owner selector (add-form only; the edit form never renders
 * this — FR-012). Fully controlled: the parent owns the subject flag + owner id
 * and the default-owner pre-selection (AS-004). When unchecked the selector is
 * hidden and the owner clears (AS-007).
 */
export function StrOwnerSelect({
    subject,
    strOwnerId,
    newStrOwner,
    owners,
    onChange,
    isLoading = false,
    isError = false,
    error = null,
}: StrOwnerSelectProps) {
    const isNewOwner = newStrOwner != null;
    const selectValue = isNewOwner ? NEW_STR_OWNER : strOwnerId == null ? "" : String(strOwnerId);
    const displayLabel = isNewOwner
        ? "＋ New owner"
        : strOwnerId == null
          ? "Select owner"
          : (owners.find((o) => o.id === strOwnerId)?.name ?? "Select owner");

    // Which control the current `error` blames, so `aria-invalid`/`aria-describedby`
    // point AT the offending field (WCAG 3.3.1/4.1.2). The component's own state
    // distinguishes the three edge cases unambiguously: inline-create in progress
    // ⇒ the new-owner inputs (EC-003); no owner chosen ⇒ the select (EC-002);
    // otherwise the subject checkbox / MyData prerequisite (EC-001).
    const errorTarget: "newOwner" | "select" | "subject" | null = error
        ? isNewOwner
            ? "newOwner"
            : strOwnerId == null
              ? "select"
              : "subject"
        : null;
    const newOwnerFieldInvalid = (value: string): boolean =>
        errorTarget === "newOwner" && value.trim() === "";

    const handleSubjectChange = (checked: boolean): void => {
        // Clearing the owner when unchecking keeps the wire body honest — a
        // not-subject apartment carries no owner (AS-007 / EC-006).
        onChange({
            subject: checked,
            strOwnerId: checked ? strOwnerId : null,
            newStrOwner: checked ? newStrOwner : null,
        });
    };

    const handleOwnerChange = (value: string): void => {
        if (value === NEW_STR_OWNER) {
            // Enter inline-create mode: reveal the three inputs, drop any picked id.
            onChange({ subject: true, strOwnerId: null, newStrOwner: EMPTY_NEW_OWNER });
            return;
        }
        onChange({ subject: true, strOwnerId: Number(value), newStrOwner: null });
    };

    const handleNewOwnerField = (field: keyof NewStrOwner, value: string): void => {
        onChange({
            subject: true,
            strOwnerId: null,
            newStrOwner: { ...(newStrOwner ?? EMPTY_NEW_OWNER), [field]: value },
        });
    };

    return (
        <Card className="h-fit">
            <CardHeader>
                <CardTitle>Short-Term Registry (STR)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                    <Checkbox
                        id={CHECKBOX_TESTID}
                        data-testid={CHECKBOX_TESTID}
                        checked={subject}
                        aria-invalid={errorTarget === "subject" ? "true" : undefined}
                        aria-describedby={errorTarget === "subject" ? ERROR_ID : undefined}
                        onCheckedChange={(checked) => handleSubjectChange(checked === true)}
                    />
                    <Label htmlFor={CHECKBOX_TESTID}>Subject to STR upload</Label>
                </div>

                {subject && (
                    <div className="space-y-2">
                        <Label htmlFor={SELECT_TESTID}>STR Owner*</Label>
                        <Select value={selectValue} disabled={isLoading || isError} onValueChange={handleOwnerChange}>
                            <SelectTrigger
                                id={SELECT_TESTID}
                                data-testid={SELECT_TESTID}
                                className="w-full"
                                aria-required="true"
                                aria-invalid={errorTarget === "select" ? "true" : undefined}
                                aria-describedby={errorTarget === "select" ? ERROR_ID : undefined}
                            >
                                <SelectValue placeholder="Select owner">{displayLabel}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {owners.map((owner) => (
                                    <SelectItem key={owner.id} value={String(owner.id)}>
                                        {ownerLabel(owner)}
                                    </SelectItem>
                                ))}
                                <SelectItem value={NEW_STR_OWNER}>＋ New owner</SelectItem>
                            </SelectContent>
                        </Select>

                        {isNewOwner && (
                            <div className="space-y-3 pt-2">
                                <div className="space-y-2">
                                    <Label htmlFor={NEW_OWNER_NAME_ID}>Owner Name*</Label>
                                    <Input
                                        id={NEW_OWNER_NAME_ID}
                                        value={newStrOwner.name}
                                        aria-required="true"
                                        aria-invalid={newOwnerFieldInvalid(newStrOwner.name) ? "true" : undefined}
                                        aria-describedby={newOwnerFieldInvalid(newStrOwner.name) ? ERROR_ID : undefined}
                                        onChange={(e) => handleNewOwnerField("name", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor={NEW_OWNER_USERNAME_ID}>Taxis Username*</Label>
                                    <Input
                                        id={NEW_OWNER_USERNAME_ID}
                                        value={newStrOwner.taxisUsername}
                                        aria-required="true"
                                        aria-invalid={newOwnerFieldInvalid(newStrOwner.taxisUsername) ? "true" : undefined}
                                        aria-describedby={newOwnerFieldInvalid(newStrOwner.taxisUsername) ? ERROR_ID : undefined}
                                        onChange={(e) => handleNewOwnerField("taxisUsername", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor={NEW_OWNER_PASSWORD_ID}>Taxis Password*</Label>
                                    <Input
                                        id={NEW_OWNER_PASSWORD_ID}
                                        type="password"
                                        autoComplete="new-password"
                                        value={newStrOwner.taxisPassword}
                                        aria-required="true"
                                        aria-invalid={newOwnerFieldInvalid(newStrOwner.taxisPassword) ? "true" : undefined}
                                        aria-describedby={newOwnerFieldInvalid(newStrOwner.taxisPassword) ? ERROR_ID : undefined}
                                        onChange={(e) => handleNewOwnerField("taxisPassword", e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <p className="text-sm text-gray-500">
                    Whether this apartment files its short-term-registry statements, and under which Taxis owner.
                </p>
                {error && (
                    <p id={ERROR_ID} role="alert" className="text-sm text-red-600">
                        {error}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
